import { useState, useEffect } from 'react';
import { calculateVehiclePriceBreakdown, normalizeVehicleType } from '../utils/pricingUtils';

/**
 * Loads the signed-in customer's pricing setup (base price, mode, SEVS fee and
 * per-vehicle-type caps) and exposes the payable total for a report.
 */
export const usePricing = (user, supabase) => {
  const [customerPrice, setCustomerPrice] = useState(null);
  const [pricingMode, setPricingMode] = useState('standard');
  const [sevsPrice, setSevsPrice] = useState(0);
  const [vehicleTypeCaps, setVehicleTypeCaps] = useState([]);

  // Fetch prices from Edge Function
  useEffect(() => {
    const fetchPrices = async () => {
      if (!user) return;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        if (!accessToken) return;

        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/calculate-price`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ user_id: user.id })
          }
        );

        const data = await res.json();

        // Edge function already applies 10% on SEVS
        let rawCustomerPrice = data.customer_price;
        const { data: userPricingData, error: userPricingError } = await supabase
          .from('users')
          .select('customer_price, pricing_mode')
          .eq('id', user.id)
          .maybeSingle();

        // If the user has a specific customer price set in the users table, use that instead of the default from the edge function. This allows for per-user overrides of pricing.
        if (!userPricingError && userPricingData) {
          rawCustomerPrice = userPricingData.customer_price;
        }

        // Normalize the customer price to a number or null if it's invalid, and update state accordingly.
        const parsedCustomerPrice = Number(rawCustomerPrice);
        setCustomerPrice(
          rawCustomerPrice === null || rawCustomerPrice === undefined || rawCustomerPrice === '' || !Number.isFinite(parsedCustomerPrice)
            ? null
            : parsedCustomerPrice
        );
        setPricingMode(userPricingData?.pricing_mode || data.pricing_mode || 'standard');
        setSevsPrice(Number(data.sevs_price || 0));
        setVehicleTypeCaps(data.vehicle_type_caps || []);

      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };

    fetchPrices();
  }, [user, supabase]);

  // Get total price for a specific type
 const getTotalPrice = (reportOrType, reportId, reportsMap) => {
  const report = typeof reportOrType === 'object'
    ? reportOrType
    : reportsMap?.[reportId] || {};
  const normalizedVehicleType = normalizeVehicleType(report.vehicle_type);
  const capData = vehicleTypeCaps.find(
    vehicleTypeCap => normalizeVehicleType(vehicleTypeCap.vehicle_type) === normalizedVehicleType
  );
  const breakdown = calculateVehiclePriceBreakdown({
    vehicleType: report.vehicle_type,
    vehiclePrice: report.vehicle_price,
    customerBasePrice: customerPrice,
    pricingMode,
    sevsFee: sevsPrice,
    cap: capData?.cap,
    isCapped: capData?.is_capped || false
  });

  // Return the total price after applying any capping adjustments, which is the price the customer will pay for this report. This ensures that if the uncapped base price exceeds the cap, the adjustment is subtracted from the beforeCapPrice to yield the final payable amount.
  return breakdown.beforeCapPrice - breakdown.adjustment;
};

  return { customerPrice, hasCustomerPrice: customerPrice !== null, pricingMode, sevsPrice, vehicleTypeCaps, getTotalPrice };
};